/**
 * TechStack Section - Technologies We Use
 * ========================================
 * 6 categories with technology tags
 */

import React, { useState, useEffect, useRef } from 'react';
import './TechStack.css';

const techCategories = [
  {
    id: 1,
    icon: 'fa-cubes',
    title: 'Blockchain',
    color: '#F97316',
    techs: ['Ethereum', 'Solana', 'Polygon', 'BSC', 'Cosmos', 'Substrate']
  },
  {
    id: 2,
    icon: 'fa-file-contract',
    title: 'Smart Contracts',
    color: '#A855F7',
    techs: ['Solidity', 'Rust', 'Vyper', 'Move', 'Hardhat', 'Foundry']
  },
  {
    id: 3,
    icon: 'fa-desktop',
    title: 'Frontend',
    color: '#00D4FF',
    techs: ['React', 'Next.js', 'Vue', 'TypeScript', 'Tailwind', 'Three.js']
  },
  {
    id: 4,
    icon: 'fa-server',
    title: 'Backend',
    color: '#10B981',
    techs: ['Node.js', 'Python', 'Go', 'GraphQL', 'PostgreSQL', 'Redis']
  },
  {
    id: 5,
    icon: 'fa-mobile-alt',
    title: 'Mobile',
    color: '#EC4899',
    techs: ['React Native', 'Flutter', 'Swift', 'Kotlin', 'Firebase']
  },
  {
    id: 6,
    icon: 'fa-cloud',
    title: 'DevOps',
    color: '#D4A853',
    techs: ['AWS', 'Docker', 'Kubernetes', 'CI/CD', 'IPFS', 'TheGraph']
  }
];

// Particles Component
const TechParticles = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];
    let nodes = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    const createElements = () => {
      const isMobile = window.innerWidth < 768;
      const particleCount = isMobile ? 40 : 80;
      const nodeCount = isMobile ? 6 : 12;

      particles = [];
      nodes = [];

      for (let i = 0; i < particleCount; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 1.5 + 0.5,
          speedX: (Math.random() - 0.5) * 0.3,
          speedY: (Math.random() - 0.5) * 0.3,
          opacity: Math.random() * 0.3 + 0.1
        });
      }

      for (let i = 0; i < nodeCount; i++) {
        nodes.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 3 + 2,
          speedX: (Math.random() - 0.5) * 0.2,
          speedY: (Math.random() - 0.5) * 0.2,
          pulse: Math.random() * Math.PI * 2
        });
      }
    };

    const animate = () => {
      ctx.fillStyle = 'rgba(5, 5, 5, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw particles
      particles.forEach(p => {
        p.x += p.speedX;
        p.y += p.speedY;

        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(212, 168, 83, ${p.opacity})`;
        ctx.fill();
      });

      // Draw nodes with connections
      nodes.forEach((node, i) => {
        node.x += node.speedX;
        node.y += node.speedY;
        node.pulse += 0.02;

        if (node.x < 0 || node.x > canvas.width) node.speedX *= -1;
        if (node.y < 0 || node.y > canvas.height) node.speedY *= -1;

        // Connections
        nodes.forEach((other, j) => {
          if (i < j) {
            const dx = node.x - other.x;
            const dy = node.y - other.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < 150) {
              ctx.beginPath();
              ctx.moveTo(node.x, node.y);
              ctx.lineTo(other.x, other.y);
              ctx.strokeStyle = `rgba(212, 168, 83, ${0.1 * (1 - dist / 150)})`;
              ctx.stroke();
            }
          }
        });

        // Draw node
        const pulseSize = node.size + Math.sin(node.pulse) * 1;
        ctx.beginPath();
        ctx.arc(node.x, node.y, pulseSize, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(212, 168, 83, 0.6)`;
        ctx.fill();
      });

      animationId = requestAnimationFrame(animate);
    };

    resize();
    createElements();
    animate();

    window.addEventListener('resize', () => {
      resize();
      createElements();
    });

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="tech-canvas" />;
};

// Tech Category Card
const TechCard = ({ category, index, isInView }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isInView) {
      const timer = setTimeout(() => setIsVisible(true), index * 100);
      return () => clearTimeout(timer);
    }
  }, [isInView, index]);

  return (
    <div className={`tech-card ${isVisible ? 'in-view' : ''}`}>
      <div className="tech-card-header">
        <div className="tech-icon" style={{ color: category.color, background: `${category.color}15`, borderColor: `${category.color}30` }}>
          <i className={`fas ${category.icon}`} />
        </div>
        <h3>{category.title}</h3>
      </div>
      <div className="tech-tags">
        {category.techs.map((tech, i) => (
          <span
            key={tech}
            className="tech-tag"
            style={{ 
              animationDelay: `${(index * 100) + (i * 50)}ms`,
              borderColor: `${category.color}25`
            }}
          >
            {tech}
          </span>
        ))}
      </div>
      <div className="tech-card-glow" style={{ background: `radial-gradient(circle at center, ${category.color}10 0%, transparent 70%)` }} />
    </div>
  );
};

const TechStack = () => {
  const sectionRef = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section className="techstack-section" ref={sectionRef} id="techstack">
      {/* Background - CSS particles */}
      <div className="techstack-bg">
        {/* Subtle glows */}
        <div className="techstack-glow techstack-glow-1" />
        <div className="techstack-glow techstack-glow-2" />
        
        {/* Particles - 20 bigger particles */}
        <div className="techstack-particles">
          {[...Array(20)].map((_, i) => (
            <span 
              key={i}
              className="particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                '--size': `${4 + Math.random() * 6}px`,
                '--duration': `${12 + Math.random() * 15}s`,
                '--delay': `${Math.random() * 8}s`
              }}
            />
          ))}
        </div>
        <div className="techstack-grid-bg" />
        <div className="techstack-vignette" />
      </div>

      <div className="techstack-container">
        {/* Header */}
        <div className={`techstack-header ${isInView ? 'in-view' : ''}`}>
          <div className="section-badge">
            <i className="fas fa-code" />
            <span>Tech Stack</span>
          </div>
          <h2 className="section-title">
            Built with <span className="text-highlight">Best-in-Class</span> Tech
          </h2>
          <p className="section-subtitle">
            We use cutting-edge technologies to build future-proof solutions
          </p>
        </div>

        {/* Tech Grid */}
        <div className="techstack-grid">
          {techCategories.map((category, index) => (
            <TechCard
              key={category.id}
              category={category}
              index={index}
              isInView={isInView}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TechStack;
